import React, { useMemo, useState } from "react"

import {
    type CalculatorResultType,
    PayerCoverageCheckNetwork,
} from "@common-shared/config/coverage.config.ts"
import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import type { IIndexable } from "@common-shared/types/general.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import {
    CHECKER_CONFIG,
    CoverageCheckerMode,
} from "../../../config/checker.config"
import { POLICIES_COLUMNS_CONFIG } from "../../../config/policiesColumns.config"
import { ExportReportHelper } from "../../../helpers/exportReport.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook.ts"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import { ResultsQuickViewChartComponent } from "./resultsQuickViewChart.component"

export const ResultsQuickViewComponent: React.FunctionComponent<
    IResultsQuickViewComponentProps
> = props => {
    const hasNetworkStatusFeatureFlag = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_NETWORK_STATUS_RULES,
    )

    const checkerState = useAppSelector(checkerSelector)

    const [hoveredResultType, setHoveredResultType] =
        useState<CalculatorResultType>()

    const resultsStats = useMemo<{
        breakdown: Partial<Record<CalculatorResultType, number>>
        dataCorrections: {
            total: number
            items: Record<string, number>
        }
        networkStatusAlerts: number
    }>(() => {
        const breakdown = {} as IIndexable

        const dataCorrections = {
            total: 0,
            items: {},
        }

        let networkStatusAlerts = 0

        const processSingleCoverageCheckHistory = (
            coverageCheck: IOneVerifyCoverageCheckHistory,
            processDataCorrections = true,
        ) => {
            const { coverageResult } = coverageCheck

            if (!coverageResult.resultType) {
                return
            }

            breakdown[coverageResult.resultType] =
                breakdown[coverageResult.resultType] || 0
            breakdown[coverageResult.resultType] += 1

            if (coverageResult.networkStatusAlert?.isAlert) {
                networkStatusAlerts += 1
            }

            // Calculate data corrections
            // Only applies to continuous monitoring checks
            POLICIES_COLUMNS_CONFIG.dataCorrectionColumns.forEach(column => {
                if (!processDataCorrections) {
                    return
                }

                // Make sure that it doesn't contain additional data
                const correctionKey = column.label.split("(")[0].trim()

                // Make sure key exists in result
                dataCorrections.items[
                    correctionKey as keyof typeof dataCorrections.items
                ] =
                    dataCorrections.items[
                        correctionKey as keyof typeof dataCorrections.items
                    ] || 0

                const columnValue = ExportReportHelper.getMappedColumnValue(
                    coverageCheck,
                    column,
                    "",
                    false,
                )

                if (columnValue) {
                    dataCorrections.total++
                    dataCorrections.items[
                        correctionKey as keyof typeof dataCorrections.items
                    ]++
                }
            })
        }

        switch (props.checkType) {
            case QuotaUsageType.GENERAL:
            case QuotaUsageType.DISCOVER:
            case QuotaUsageType.SMART_SCAN:
            case QuotaUsageType.AGENTIC_PORTAL:
            case QuotaUsageType.AGENTIC_PHONE:
                checkerState.continuousMonitoringCoverageChecks.forEach(
                    check => {
                        for (const network of [
                            PayerCoverageCheckNetwork.IN,
                            PayerCoverageCheckNetwork.OUT,
                        ]) {
                            const coverageCheckHistory:
                                | IOneVerifyCoverageCheckHistory
                                | undefined
                                | null =
                                check.result?.[
                                    network as keyof typeof check.result
                                ]

                            coverageCheckHistory &&
                                processSingleCoverageCheckHistory(
                                    coverageCheckHistory,
                                )
                        }
                    },
                )

                break

            case QuotaUsageType.MEDICAID:
                checkerState.medicaidChecks.forEach(check => {
                    const coverageCheckHistory = check.result

                    coverageCheckHistory &&
                        processSingleCoverageCheckHistory(
                            coverageCheckHistory,
                            false,
                        )
                })

                break
        }

        return {
            breakdown,
            dataCorrections,
            networkStatusAlerts,
        }
    }, [
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.medicaidChecks,
        props.checkType,
    ])

    const resultsCount = useMemo<{
        total: number
        successful: number
        failed: number
    }>(() => {
        let total = 0

        switch (props.checkType) {
            case QuotaUsageType.GENERAL:
            case QuotaUsageType.DISCOVER:
            case QuotaUsageType.SMART_SCAN:
            case QuotaUsageType.AGENTIC_PORTAL:
            case QuotaUsageType.AGENTIC_PHONE:
                total =
                    checkerState.continuousMonitoringCoverageChecks?.reduce(
                        (accumulator, item) =>
                            accumulator +
                            (item.result?.IN ? 1 : 0) +
                            (item.result?.OUT ? 1 : 0),
                        0,
                    ) || 0

                break

            case QuotaUsageType.MEDICAID:
                total = checkerState.medicaidChecks.filter(
                    item => item.result,
                ).length

                break
        }

        const successful = CHECKER_CONFIG.quickResultsSuccessfulResults.reduce(
            (accumulator, item) => {
                accumulator += resultsStats.breakdown[item] || 0
                return accumulator
            },
            0,
        )

        return {
            total,
            successful,
            failed: total - successful,
        }
    }, [resultsStats.breakdown, props.checkType])

    const getFormattedPercentage = (amount: number, total?: number): string => {
        return ((amount / (total || resultsCount.total)) * 100).toFixed(2)
    }

    const getResultTypeRendering = (
        resultType: string,
        index: number,
    ): JSX.Element | null => {
        if (
            checkerState.checkerMode === CoverageCheckerMode.CSV &&
            CHECKER_CONFIG.quickViewExcludedResultTypes.includes(
                resultType as CalculatorResultType,
            )
        ) {
            return null
        }

        const resultTypeCount =
            resultsStats.breakdown[resultType as CalculatorResultType]

        return (
            <div
                key={index}
                className="pt-[24px] flex items-center cursor-help"
                onMouseEnter={() => {
                    setHoveredResultType(resultType as CalculatorResultType)
                }}
                onMouseLeave={() => {
                    setHoveredResultType(undefined)
                }}
            >
                <div
                    className="mr-[8px] w-[16px] h-[16px] rounded-[4px] relative top-[-2px]"
                    style={{
                        backgroundColor:
                            CHECKER_CONFIG.calculatorResultTypeColorMapping[
                                resultType as CalculatorResultType
                            ],
                    }}
                />

                <PrimaryText className="flex-1 mr-[16px]">
                    {
                        CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                            resultType as CalculatorResultType
                        ]
                    }
                </PrimaryText>

                <PrimaryText>
                    {resultTypeCount ? (
                        <>
                            <span className="text-bold">{resultTypeCount}</span>{" "}
                            ({getFormattedPercentage(resultTypeCount)}
                            %)
                        </>
                    ) : (
                        "-"
                    )}
                </PrimaryText>
            </div>
        )
    }

    return resultsCount.total ? (
        <div
            className={`
                relative bg-brand-white
                pl-[40px] pr-[24px] py-[40px]
                ${props.className}
            `}
        >
            <div className="flex md:block">
                <div className="w-[400px] md:w-full">
                    <PrimaryText
                        size={29}
                        lineHeight={35}
                        weight="bold"
                        centered
                    >
                        Total Checks: {resultsCount.total}
                    </PrimaryText>

                    <ResultsQuickViewChartComponent
                        className="mt-[16px] mx-auto"
                        totalChecks={resultsCount.total}
                        resultsBreakdown={resultsStats.breakdown}
                        widthPercentage={60}
                        hoveredResultType={hoveredResultType}
                    />
                </div>

                <div className="flex-1 pt-[8px] md:pt-[0px] md:mt-[24px]">
                    <div className="flex md:block">
                        <div className="flex-1 px-[24px] mr-[16px] md:mt-[24px] md:mr-[0px]">
                            <PrimaryText typography="subtitle">
                                <span className="text-bold">
                                    Successful: {resultsCount.successful}
                                </span>{" "}
                                (
                                {getFormattedPercentage(
                                    resultsCount.successful,
                                )}
                                %)
                            </PrimaryText>

                            {Object.keys(resultsStats.breakdown)
                                .filter(item =>
                                    CHECKER_CONFIG.quickResultsSuccessfulResults.includes(
                                        item as CalculatorResultType,
                                    ),
                                )
                                .map(getResultTypeRendering)}
                        </div>

                        <div className="flex-1 px-[24px] md:mt-[24px]">
                            <PrimaryText typography="subtitle">
                                <span className="text-bold">
                                    Unsuccessful: {resultsCount.failed}
                                </span>{" "}
                                ({getFormattedPercentage(resultsCount.failed)}%)
                            </PrimaryText>

                            {Object.keys(resultsStats.breakdown)
                                .filter(
                                    item =>
                                        !CHECKER_CONFIG.quickResultsSuccessfulResults.includes(
                                            item as CalculatorResultType,
                                        ),
                                )
                                .map(getResultTypeRendering)}
                        </div>
                    </div>

                    {hasNetworkStatusFeatureFlag &&
                    resultsStats.networkStatusAlerts ? (
                        <>
                            {/*DIVIDER*/}
                            <div className="flex h-[1px] mx-[24px] my-[32px] bg-brand-grey5 md:hidden" />

                            <div className="px-[24px] md:mt-[24px]">
                                <PrimaryText typography="subtitle">
                                    <span className="text-bold">
                                        Network Status Alerts:{" "}
                                        {resultsStats.networkStatusAlerts}
                                    </span>{" "}
                                    (
                                    {getFormattedPercentage(
                                        resultsStats.networkStatusAlerts,
                                    )}
                                    %)
                                </PrimaryText>
                            </div>
                        </>
                    ) : null}

                    {resultsStats.dataCorrections.total ? (
                        <>
                            {/*DIVIDER*/}
                            <div className="flex h-[1px] mx-[24px] my-[32px] bg-brand-grey5 md:hidden" />

                            <div className="mt-[24px]">
                                <PrimaryText
                                    typography="subtitle"
                                    className="px-[24px]"
                                >
                                    <span className="text-bold">
                                        Data Corrections:{" "}
                                        {resultsStats.dataCorrections.total}
                                    </span>
                                </PrimaryText>

                                <div className="flex flex-wrap md:block">
                                    {Object.keys(
                                        resultsStats.dataCorrections.items,
                                    ).map((key, index) => {
                                        const correctionCount =
                                            resultsStats.dataCorrections.items[
                                                key
                                            ]

                                        return (
                                            <div
                                                key={index}
                                                className="flex-basis-50 flex items-center pt-[24px] px-[24px]"
                                            >
                                                <PrimaryText className="flex-1 mr-[16px]">
                                                    {key}
                                                </PrimaryText>

                                                <PrimaryText>
                                                    {correctionCount ? (
                                                        <>
                                                            <span className="text-bold">
                                                                {
                                                                    correctionCount
                                                                }
                                                            </span>{" "}
                                                            (
                                                            {getFormattedPercentage(
                                                                correctionCount,
                                                                resultsStats
                                                                    .dataCorrections
                                                                    .total,
                                                            )}
                                                            %)
                                                        </>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </PrimaryText>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    ) : null
}

interface IResultsQuickViewComponentProps {
    className?: string
    checkType: QuotaUsageType
}
